<template>
  <b-container>
    <div class="shipping-costs-calculator-view-wrap">
      <Stepper
        :steps="calculatorSteps"
        :activeStepIndex="activeStepIndex"
        :handleStepClick="goToStep"
      >
        <div slot="stepper-content" class="cn-23-form-wrap">
          <div class="step-wrap" v-if="activeStepIndex === 0">
            <transition-group name="fade" tag="div" style="max-width: 100%">
              <shipping-costs-route-item
                v-for="(item, index) in routeItems"
                :key="`item-${index}`"
                :index="index"
                :$v="$v.routeItems.$each.$iter[index]"
                :item="item"
                :hasFormError="hasFormError"
                @delete-item="deleteItem(index)"
                :is-last-item="index === routeItems.length - 1"
                @update-item="(data) => updateItem(index, data)"
                @add-item="addItem(index)"
              ></shipping-costs-route-item>
            </transition-group>
          </div>

          <div class="step-wrap" v-if="activeStepIndex === 1">
            <div class="form-section form-section--small">
              <div class="shipping-costs-calculator__boxes-wrap">
                <div class="shipping-costs-calculator__box-wrap">
                  <button
                    class="shipping-costs-calculator__box shipping-costs-calculator__box--envelope"
                    :class="{
                      'shipping-costs-calculator__box--active':
                        selectedBox === 'envelope',
                    }"
                    @click="setBoxDefaults('envelope')"
                  >
                    <font-awesome-icon
                      :icon="['fal', 'envelope']"
                      class="shipping-costs-calculator__box-icon"
                    />
                    <span class="shipping-costs-calculator__box-text">{{
                      $t("Envelope")
                    }}</span>
                  </button>
                </div>
                <div class="shipping-costs-calculator__box-wrap">
                  <button
                    class="shipping-costs-calculator__box shipping-costs-calculator__box--small"
                    :class="{
                      'shipping-costs-calculator__box--active':
                        selectedBox === 'small',
                    }"
                    @click="setBoxDefaults('small')"
                  >
                    <font-awesome-icon
                      :icon="['fal', 'box']"
                      class="shipping-costs-calculator__box-icon"
                    />
                    <span class="shipping-costs-calculator__box-text">{{
                      $t("Small box")
                    }}</span>
                  </button>
                </div>
                <div class="shipping-costs-calculator__box-wrap">
                  <button
                    class="shipping-costs-calculator__box shipping-costs-calculator__box--medium"
                    :class="{
                      'shipping-costs-calculator__box--active':
                        selectedBox === 'medium',
                    }"
                    @click="setBoxDefaults('medium')"
                  >
                    <font-awesome-icon
                      :icon="['fal', 'box']"
                      class="shipping-costs-calculator__box-icon"
                    />
                    <span class="shipping-costs-calculator__box-text">{{
                      $t("Medium box")
                    }}</span>
                  </button>
                </div>
                <div class="shipping-costs-calculator__box-wrap">
                  <button
                    class="shipping-costs-calculator__box shipping-costs-calculator__box--large"
                    :class="{
                      'shipping-costs-calculator__box--active':
                        selectedBox === 'large',
                    }"
                    @click="setBoxDefaults('large')"
                  >
                    <font-awesome-icon
                      :icon="['fal', 'box-alt']"
                      class="shipping-costs-calculator__box-icon"
                    />
                    <span class="shipping-costs-calculator__box-text">{{
                      $t("Large box")
                    }}</span>
                  </button>
                </div>
              </div>
              <div class="shipping-costs-calculator__box-size-input-wrap">
                <b-form-group
                  :label="`${$t('Length')} (cm)`"
                  label-for="box-length"
                >
                  <b-form-input
                    class="custom-input"
                    id="box-length"
                    v-model="$v.boxLength.$model"
                    :state="$v.boxLength.$dirty ? !$v.boxLength.$error : null"
                    trim
                    type="text"
                  ></b-form-input>
                </b-form-group>
                <span class="shipping-costs-calculator__box-size-separator"
                  >x</span
                >
                <b-form-group
                  :label="`${$t('Height')} (cm)`"
                  label-for="box-height"
                >
                  <b-form-input
                    class="custom-input"
                    id="box-height"
                    v-model="$v.boxHeight.$model"
                    :state="$v.boxHeight.$dirty ? !$v.boxHeight.$error : null"
                    trim
                    type="text"
                  ></b-form-input>
                </b-form-group>
                <span class="shipping-costs-calculator__box-size-separator"
                  >x</span
                >
                <b-form-group
                  :label="`${$t('Width')} (cm)`"
                  label-for="box-width"
                >
                  <b-form-input
                    class="custom-input"
                    id="box-width"
                    v-model="$v.boxWidth.$model"
                    :state="$v.boxWidth.$dirty ? !$v.boxWidth.$error : null"
                    trim
                    type="text"
                  ></b-form-input>
                </b-form-group>
              </div>
              <b-form-group
                :label="$t('Package weight (kg)')"
                label-for="weight"
              >
                <b-form-input
                  class="custom-input"
                  id="weight"
                  v-model="$v.boxWeight.$model"
                  :state="$v.boxWeight.$dirty ? !$v.boxWeight.$error : null"
                  trim
                  type="text"
                ></b-form-input>
              </b-form-group>
              <hr />

              <b-form-group
                :label="`${$t('Business name')} *`"
                label-for="destinationBusinessName"
              >
                <b-form-input
                  class="custom-input"
                  id="businessName"
                  v-model="$v.businessName.$model"
                  :state="
                    $v.businessName.$dirty || hasFormError
                      ? !$v.businessName.$invalid
                      : null
                  "
                  trim
                  type="text"
                  min="1"
                ></b-form-input>
              </b-form-group>

              <b-form-group
                :label="`${$t('Email address')} *`"
                label-for="emailAddress"
              >
                <b-form-input
                  class="custom-input"
                  id="emailAddress"
                  type="email"
                  v-model="$v.emailAddress.$model"
                  trim
                  :state="
                    $v.emailAddress.$dirty || hasFormError
                      ? !$v.emailAddress.$invalid
                      : null
                  "
                ></b-form-input>
              </b-form-group>

              <b-form-group :label="$t('Phone number')" label-for="phoneNumber">
                <b-form-input
                  class="custom-input"
                  id="phoneNumber"
                  v-model="phoneNumber"
                  trim
                  type="text"
                  min="1"
                ></b-form-input>
              </b-form-group>

              <b-form-group :description="$t('consent-text')">
                <b-form-checkbox
                  name="consent"
                  v-model="hasGivenConsent"
                  :state="hasFormError ? $v.hasGivenConsent.$model : null"
                >
                  {{
                    $t(
                      "I agree to receive communications from Sendcloud and I agree to the"
                    )
                  }}
                  <a
                    href="https://www.sendcloud.com/privacy-policy/"
                    class="privacy-policy-link"
                    target="_blank"
                    >{{ $t("Sendcloud privacy policy") }}</a
                  >.
                </b-form-checkbox>
              </b-form-group>
            </div>
          </div>
          <div class="step-wrap" v-if="activeStepIndex === 2">
            <div class="form-section">
              <shipping-costs-results
                :route-items="routeItems"
                :shipping-methods="shippingMethods"
                :weight="boxWeight"
                :volume="totalVolume"
                v-if="!isLoading"
              />
              <div class="shipping-costs__results-loader" v-else-if="isLoading">
                <transition name="fade" mode="out-in">
                  <img
                    :src="currentLoadingLogo"
                    class="shipping-costs__carrier-logo"
                    :key="currentLoadingLogo"
                  />
                </transition>
                {{ $t("Fetching the best prices...") }}
              </div>
              <b-alert show variant="danger" v-else>
                {{ $t("Something went wrong.😕 Please try again later.") }}
              </b-alert>
            </div>
          </div>
        </div>
      </Stepper>
      <b-alert show variant="danger" v-if="hasGeneratingError">
        {{
          $t(
            "Something went wrong while fetching the prices. 😕 Please try again later."
          )
        }}
      </b-alert>
      <b-alert show variant="warning" v-if="hasFormError">
        {{
          $t("Not all fields were filled correctly. Please check your input.")
        }}
      </b-alert>
      <button
        class="continue-button"
        @click="goToStep(activeStepIndex + 1)"
        v-if="activeStepIndex === 0"
      >
        {{ $t("Continue") }}
      </button>
      <button
        class="continue-button"
        @click="calculatePricing()"
        v-if="activeStepIndex === 1"
      >
        {{ $t("Calculate shipping costs") }}
      </button>
      <small class="disclaimer-text">{{
        $t(
          "Note: some carriers calculate their prices based on parameters other than those used in this calculation, such as volumetric weight and surcharges. We cannot guarantee that the outcome of this calculation is correct. It is intended to give you an indication. You cannot derive any rights from it. Consult the help center for specific carrier details and fees."
        )
      }}</small>
      <small class="disclaimer-text">{{ $t(`disclaimer-text`) }}</small>
    </div>
  </b-container>
</template>

<script>
import Stepper from "@/components/generic/Stepper.vue";
import ShippingCostsRouteItem from "@/components/ShippingCostsCalculator/ShippingCostsRouteItem";
import ShippingCostsResults from "@/components/ShippingCostsCalculator/ShippingCostsResults";
import { BContainer } from "bootstrap-vue";
import Vue from "vue";
import axios from "axios";
import Vuelidate from "vuelidate";
import { required } from "vuelidate/lib/validators";
Vue.use(Vuelidate);
import { getCurrentLanguage } from "@/lang/i18n.js";
import { getShippingMethods } from "@/services/shipping-costs-calculator.service";

export default {
  name: "ShippingCostsCalculator",
  components: {
    Stepper,
    BContainer,
    ShippingCostsRouteItem,
    ShippingCostsResults,
  },
  validations: {
    routeItems: {
      required,
      $each: {
        volume: {
          required,
        },
        originCountry: {
          required,
        },
        destinationCountry: {
          required,
        },
      },
    },
    boxWeight: {
      required,
    },
    boxWidth: {
      required,
    },
    boxHeight: {
      required,
    },
    boxLength: {
      required,
    },
    emailAddress: {
      required,
    },
    businessName: {
      required,
    },
    hasGivenConsent: {
      required,
    },
  },
  data() {
    return {
      timer: null,
      activeStepIndex: 0,
      isLoading: false,
      hasFormError: false,
      hasGeneratingError: false,
      hasGivenConsent: false,
      selectedBox: null,
      shippingMethods: [],
      volumeRanges: [
        { value: 50, maxVolume: 100 },
        { value: 200, maxVolume: 300 },
        { value: 650, maxVolume: 1000 },
        { value: 1750, maxVolume: 2500 },
        { value: 3750, maxVolume: 5000 },
        { value: 7500, maxVolume: 10000 },
        { value: 10000, maxVolume: Infinity },
      ],
      boxSizeWeightDefaults: {
        envelope: {
          length: 5,
          width: 7,
          height: 1,
          weight: 0.5,
        },
        small: {
          length: 5,
          width: 9,
          height: 3,
          weight: 2.5,
        },
        medium: {
          length: 20,
          width: 30,
          height: 40,
          weight: 10,
        },
        large: {
          length: 60,
          width: 50,
          height: 40,
          weight: 25,
        },
      },
      routeItems: [
        {
          volume: 100,
          originCountry: "Netherlands",
          originCountryShortcode: "NL",
          destinationCountry: "Germany",
          destinationCountryShortcode: "DE",
        },
      ],
      calculatorSteps: [
        {
          title: this.$t("Shipping routes"),
          heading: this.$t("List your shipping routes"),
          description: this.$t(
            "List the destinations you're shipping to often. We'll use this to pick the best carriers for these routes."
          ),
          icon: "route",
        },
        {
          title: this.$t("Packaging type"),
          heading: this.$t("Packaging type"),
          description: this.$t(
            "Specify the size and weight of the boxes you use. We'll use this to pick the right carriers."
          ),
          icon: "box",
        },
        {
          title: this.$t("Calculate shipping costs"),
          heading: this.$t("Your shipping costs with Sendcloud"),
          description: this.$t(
            "Shipping costs with Sendcloud based on your routes, box and selected plan."
          ),
          icon: "file-invoice",
        },
      ],
      orderVolume: null,
      boxLength: null,
      boxWidth: null,
      boxHeight: null,
      boxWeight: null,
      emailAddress: null,
      phoneNumber: null,
      businessName: null,
      sendcloudPlan: null,
      //Loading texts
      loadingLogos: [
        require("@/assets/carrier_logos/bpost.svg"),
        require("@/assets/carrier_logos/chronopost.svg"),
        require("@/assets/carrier_logos/colisprive.svg"),
        require("@/assets/carrier_logos/dhl.svg"),
        require("@/assets/carrier_logos/gls_de.svg"),
        require("@/assets/carrier_logos/lettresuivie.svg"),
        require("@/assets/carrier_logos/mondial_relay.svg"),
        require("@/assets/carrier_logos/postat.svg"),
        require("@/assets/carrier_logos/postnl_fulfilment.svg"),
        require("@/assets/carrier_logos/postnl.svg"),
        require("@/assets/carrier_logos/rjp.svg"),
        require("@/assets/carrier_logos/dhl_express.svg"),
        require("@/assets/carrier_logos/ups.svg"),
      ],
      loadingTextIndex: 0,
      loadingTexts: [
        "Finding the best carriers",
        "Negotiating carrier prices",
        "Sorting prices",
        "Putting it all together",
      ],
      currentLanguage: getCurrentLanguage().toUpperCase(),
      countriesForPreselect: {
        FR: { code: "FR", name: "France" },
        NL: { code: "NL", name: "Netherlands" },
        BE: { code: "BE", name: "Belgium" },
        DE: { code: "DE", name: "Germany" },
        IT: { code: "IT", name: "Italy" },
        ES: { code: "ES", name: "Spain" },
        EN: { code: "GB", name: "United Kingdom" },
      },
    };
  },
  computed: {
    currentLoadingLogo() {
      return this.loadingLogos[this.loadingTextIndex];
    },
    totalVolume() {
      return this.routeItems.reduce(
        (a, b) => parseInt(a) + parseInt(b.volume) || 0,
        0
      );
    },
    totalVolumeFromRange() {
      const range = this.volumeRanges.find(
        (range) => range.maxVolume > this.totalVolume
      );
      return range.value;
    },
    formData() {
      return {
        emailAddress: this.emailAddress,
        phoneNumber: this.phoneNumber,
        businessName: this.businessName,
        hubspotCountry: this.$root.hubspotCountry,
        "Shipping Volume": this.totalVolumeFromRange,
        tool: "Shipping Costs Calculator",
      };
    },
  },
  methods: {
    async calculatePricing() {
      if (this.$v.$invalid) {
        this.hasFormError = true;
        return;
      }

      this.cycleLoadingTexts();
      this.isLoading = true;
      this.hasError = false;
      this.hasFormError = false;
      this.goToStep(this.activeStepIndex + 1);

      try {
        const methods = await getShippingMethods({
          origins: this.routeItems.map((item) => item.originCountryShortcode),
          weight: this.boxWeight,
          destinations: this.routeItems.map(
            (item) => item.destinationCountryShortcode
          ),
        });
        this.shippingMethods = Object.freeze(methods);
        // Post to zapier
        await axios.post(
          "https://europe-west1-sendcloud-demos.cloudfunctions.net/zapier-proxy-pricing-calculator",
          this.formData
        );

        // Track Segment
        analytics.track("Shipping Costs Calculator used", this.formData); //eslint-disable-line
      } catch (e) {
        this.hasError = true;
      } finally {
        this.isLoading = false;
        this.hasFormError = false;
        clearTimeout(this.timer);
      }
    },
    cycleLoadingTexts() {
      this.loadingTextIndex = Math.floor(Math.random() * 14);
      this.timer = setInterval(() => {
        if (this.loadingTextIndex < this.loadingLogos.length - 1) {
          this.loadingTextIndex = this.loadingTextIndex + 1;
        } else {
          this.loadingTextIndex = 0;
        }
      }, 1000);
    },
    setBoxDefaults(type) {
      const { width, length, height, weight } =
        this.boxSizeWeightDefaults[type];
      this.boxWidth = width;
      this.boxHeight = height;
      this.boxLength = length;
      this.boxWeight = weight;

      this.selectedBox = type;
    },
    addItem() {
      const defaultCountry = this.countriesForPreselect[this.currentLanguage];
      this.routeItems.push({
        volume: 50,
        originCountry: defaultCountry.name,
        originCountryShortcode: defaultCountry.code,
        destinationCountry: defaultCountry.name,
        destinationCountryShortcode: defaultCountry.code,
      });
    },
    updateItem(index, data) {
      this.$set(this.routeItems, index, data);
    },
    deleteItem(index) {
      if (this.routeItems.length === 1) return;
      this.routeItems.splice(index, 1);
    },
    goToStep(step) {
      if (step > 2) return;
      this.activeStepIndex = step;
    },
  },
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.shipping-costs-calculator-view-wrap {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 100%;
  padding-bottom: 30px;
  background: rgba(255, 255, 255, 1);
  font-family: "Roboto", sans-serif;
}

.shipping-costs__results-loader {
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.shipping-costs__carrier-logo {
  height: 45px;
  margin-bottom: 15px;
}

.cn-23-form-wrap,
.step-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.form-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 957px;
  min-height: 591px;
  margin: 0px;
  padding: 40px 0px 40px 0px;
  line-height: 1em;
}

.form-section {
  width: 100%;
}

.form-section--small {
  max-width: 600px;
}

.button {
  position: relative;
  width: 193.5px;
  min-height: 40px;
  margin: 20px 0px 0px 0px;
  background: rgb(18, 40, 87);
  border: 1px none black;
  border-radius: 5px;
  transition: background 0.25s ease-in-out;
  font-weight: 500;
  font-size: 19px;
  line-height: 1em;
  color: white;
}

.continue-button {
  position: relative;
  min-width: 120px;
  min-height: 40px;
  margin: 20px 0px 0px 0px;
  background: rgb(18, 40, 87);
  border: 1px none black;
  border-radius: 45px;
  transition: background 0.25s ease-in-out;
  font-weight: 500;
  font-size: 19px;
  line-height: 1em;
  text-align: center;
  padding: 7.5px 15px;
  color: white;
  transition: transform 0.15s;
}

hr {
  margin: 30px 0;
}

.continue-button:active {
  background: rgb(23, 51, 112);
  transform: scale(1.05, 1.05);
}

.continue-button:focus {
  box-shadow: 0px 0px 2px 3px rgba(40, 91, 202, 0.3);
}

.continue-button:active,
.continue-button:focus {
  outline: none;
}

.shipping-costs-calculator__boxes-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 60px;
  flex-wrap: wrap;
}

.shipping-costs-calculator__box-wrap {
  padding: 5px;
}

.shipping-costs-calculator__box {
  display: flex;
  flex-direction: column;
  position: relative;
  justify-content: flex-end;
  align-items: center;
  width: 135px;
  height: 135px;
  padding: 10px;
  padding-bottom: 45px;
  border: 1px solid rgb(240, 240, 240);
  border-radius: 10px;
  background: transparent;
  color: inherit;
  text-align: center;
  transition: background-color 0.2s, transform 0.2s;
}

.shipping-costs-calculator__box:hover {
  background-color: rgb(240, 244, 246);
}

.shipping-costs-calculator__box:active {
  transform: scale(1.08);
  outline: none;
}

.shipping-costs-calculator__box:focus,
.shipping-costs-calculator__box--active {
  outline: none;
  background-color: rgb(240, 244, 246);
  box-shadow: inset 0px 0px 0px 5px #1d96ff48;
}

.shipping-costs-calculator__box-icon {
  margin-bottom: 10px;
  font-size: 32px;
}

.shipping-costs-calculator__box-text {
  position: absolute;
  bottom: 15px;
}

.shipping-costs-calculator__box--small .shipping-costs-calculator__box-icon {
  font-size: 35px;
}

.shipping-costs-calculator__box--medium .shipping-costs-calculator__box-icon {
  font-size: 45px;
}

.shipping-costs-calculator__box--large .shipping-costs-calculator__box-icon {
  font-size: 55px;
}

.shipping-costs-calculator__box-size-input-wrap {
  display: flex;
  align-items: center;
}
.shipping-costs-calculator__box-size-input-wrap .form-group {
  flex-grow: 1;
}

.shipping-costs-calculator__box-size-separator {
  padding: 10px;
}

.disclaimer-text {
  margin-top: 45px;
  max-width: 100%;
}

.disclaimer-text:last-child {
  margin-top: 15px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

@media screen and (max-width: 768px) {
  .shipping-costs-calculator__box-wrap {
    flex-grow: 0;
  }
}
</style>
